import { useMemo, useState } from 'react';
import { Alert, Button, Field, Select, Textarea } from '@aigovops/design-system';
import type { ResearchCadence, ResearchFetchStrategy, ResearchSourceKind } from '@aigovops/core';
import { createProposedChange } from '../lib/api';

/**
 * Inline form for raising a `target_type=research_source, change_type=create`
 * proposal. Landing surface: the Queue toolbar (see Queue.tsx). The submit
 * writes an admin_edit proposal that the reviewer then approves+applies
 * through the standard queue flow; on apply, ProposedChangeService forces
 * is_active=1 and stamps approved_at (ADR-0002).
 *
 * Fields cover every column research_sources needs at create time. Cadence
 * defaults to the per-kind default the ResearchSourceService already uses
 * (legislation/breach → daily; framework/case_law/vendor_* → weekly) so
 * an editor filling this out only sets kind and gets the right cadence
 * for free — override remains available for one-off exceptions.
 */

const KIND_OPTIONS: { value: ResearchSourceKind; label: string; defaultCadence: ResearchCadence }[] = [
  { value: 'legislation', label: 'Legislation (daily)', defaultCadence: 'daily' },
  { value: 'framework', label: 'Framework (weekly)', defaultCadence: 'weekly' },
  { value: 'breach_intel', label: 'Breach intel (daily)', defaultCadence: 'daily' },
  { value: 'case_law', label: 'Case law (weekly)', defaultCadence: 'weekly' },
  { value: 'vendor_catalog', label: 'Vendor catalog (weekly)', defaultCadence: 'weekly' },
  { value: 'vendor_sentiment', label: 'Vendor sentiment (weekly)', defaultCadence: 'weekly' },
];
const FETCH_STRATEGY_OPTIONS: { value: ResearchFetchStrategy; label: string }[] = [
  { value: 'html', label: 'HTML scrape' },
  { value: 'rss', label: 'RSS / Atom feed' },
  { value: 'json_api', label: 'JSON API' },
  { value: 'pdf', label: 'PDF document' },
];
const CADENCE_OPTIONS: { value: ResearchCadence; label: string }[] = [
  { value: 'hourly', label: 'Hourly' },
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'on_demand', label: 'On demand (manual trigger only)' },
];

export function ProposeSourceForm(props: { onDone: () => void; onCancel: () => void }) {
  const [kind, setKind] = useState<ResearchSourceKind>('legislation');
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [jurisdiction, setJurisdiction] = useState('');
  const [fetchStrategy, setFetchStrategy] = useState<ResearchFetchStrategy>('html');
  const [cadence, setCadence] = useState<ResearchCadence>('daily');
  // Track whether the user manually touched cadence — if not, follow the
  // kind's default so a change to kind updates cadence automatically.
  const [cadenceTouched, setCadenceTouched] = useState(false);
  const [description, setDescription] = useState('');
  const [rationale, setRationale] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const urlValid = useMemo(() => isLikelyUrl(url), [url]);
  const canSubmit = kind && name.trim() && url.trim() && urlValid && rationale.trim() && !busy;

  const changeKind = (next: ResearchSourceKind) => {
    setKind(next);
    if (!cadenceTouched) {
      const def = KIND_OPTIONS.find((o) => o.value === next)?.defaultCadence;
      if (def) setCadence(def);
    }
  };

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    try {
      await createProposedChange({
        target_type: 'research_source',
        change_type: 'create',
        ai_rationale: rationale.trim(),
        proposed_state: {
          kind,
          name: name.trim(),
          url: url.trim(),
          description: description.trim() || null,
          jurisdiction: jurisdiction.trim() || null,
          fetch_strategy: fetchStrategy,
          cadence,
          // Empty JSON — reviewers can add config on approval if needed.
          // Scanners tolerate {} (see research-workflow-runner.ts).
          config: {},
        },
      });
      props.onDone();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      style={{
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-card)',
        padding: 'var(--space-4)',
        background: 'var(--surface)',
        display: 'grid',
        gap: 'var(--space-3)',
      }}
    >
      <div
        style={{
          fontSize: 'var(--font-size-md)',
          fontWeight: 600,
          color: 'var(--text)',
          fontFamily: 'var(--font-ui)',
        }}
      >
        Propose new research source
      </div>
      <p style={{ margin: 0, fontSize: 'var(--font-size-2xs)', color: 'var(--text-muted)' }}>
        Raises a source proposal into the review queue with{' '}
        <code style={{ fontFamily: 'var(--font-mono)' }}>source_kind=admin_edit</code>. On approval
        the source is activated for the next scheduler tick and its{' '}
        <code style={{ fontFamily: 'var(--font-mono)' }}>approved_at</code> stamp starts the 30-day
        probation window (ADR-0002).
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 'var(--space-3)' }}>
        <Field id="ps-kind" label="Kind">
          <Select id="ps-kind" value={kind} onChange={(e) => changeKind(e.target.value as ResearchSourceKind)}>
            {KIND_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </Select>
        </Field>
        <Field id="ps-fetch" label="Fetch strategy">
          <Select
            id="ps-fetch"
            value={fetchStrategy}
            onChange={(e) => setFetchStrategy(e.target.value as ResearchFetchStrategy)}
          >
            {FETCH_STRATEGY_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </Select>
        </Field>
        <Field id="ps-cadence" label="Cadence">
          <Select
            id="ps-cadence"
            value={cadence}
            onChange={(e) => {
              setCadence(e.target.value as ResearchCadence);
              setCadenceTouched(true);
            }}
          >
            {CADENCE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </Select>
        </Field>
      </div>

      <Field id="ps-name" label="Name">
        <input
          id="ps-name"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder='e.g. "CourtListener AI Opinions (v4 API, authenticated)"'
          style={inputStyle}
        />
      </Field>

      <Field id="ps-url" label="URL">
        <div style={{ display: 'grid', gap: '4px' }}>
          <input
            id="ps-url"
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://…"
            style={{
              ...inputStyle,
              borderColor: url && !urlValid ? 'var(--critical)' : 'var(--border)',
            }}
          />
          {url && !urlValid ? (
            <span style={{ fontSize: 'var(--font-size-3xs)', color: 'var(--critical)' }}>
              Must be a valid http(s) URL.
            </span>
          ) : null}
        </div>
      </Field>

      <Field id="ps-jurisdiction" label="Jurisdiction (optional)">
        <input
          id="ps-jurisdiction"
          type="text"
          value={jurisdiction}
          onChange={(e) => setJurisdiction(e.target.value)}
          placeholder='e.g. "US", "EU", "US-CA"'
          style={inputStyle}
        />
      </Field>

      <Field id="ps-description" label="Description (optional)">
        <Textarea
          id="ps-description"
          rows={2}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="What does this source publish, and how often?"
        />
      </Field>

      <Field id="ps-rationale" label="Why add this source?">
        <Textarea
          id="ps-rationale"
          rows={3}
          value={rationale}
          onChange={(e) => setRationale(e.target.value)}
          placeholder="Required — audit trail. e.g. Fills the case-law gap left by CourtListener v3 auth failure; higher-signal than Justia."
        />
      </Field>

      {error ? <Alert variant="error">{error}</Alert> : null}

      <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
        <Button variant="primary" compact disabled={!canSubmit} onClick={submit}>
          {busy ? 'Submitting…' : 'Raise source proposal'}
        </Button>
        <Button variant="ghost" compact disabled={busy} onClick={props.onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

// Lightweight URL sanity check — full URL parsing via `new URL()` catches
// scheme + host, which is what we need to spare the reviewer a bounced
// proposal for a typo. Not a substitute for the scanner's fetch-time check.
function isLikelyUrl(v: string): boolean {
  if (!v.trim()) return false;
  try {
    const u = new URL(v.trim());
    return u.protocol === 'http:' || u.protocol === 'https:';
  } catch {
    return false;
  }
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: 'var(--space-2) var(--space-3)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius-input, var(--radius-card))',
  background: 'var(--surface)',
  color: 'var(--text)',
  fontFamily: 'var(--font-ui)',
  fontSize: 'var(--font-size-2xs)',
};
